import React, { useState } from 'react';
import { useThemeStyles } from '../context/ThemeContext';

const DARK = {
  bg: '#0d0f18',
  cardBg: '#12141f',
  footerBg: '#0a0c15',
  border: '#1e2035',
  borderIn: '#1a1c2e',
  inputAcc: '#5badf0',
  inputBg: '#0e1f35',
  inputBdr: '#153358',
  contextAcc: '#13c2c2',
  contextBg: '#091a1a',
  contextBdr: '#13c2c230',
  outputAcc: '#b07af5',
  outputBg: '#1e0d28',
  outputBdr: '#3d1a5c',
  green: '#22c97a',
  greenBg: '#0a2218',
  amber: '#f0a044',
  amberBg: '#1e1200',
  red: '#ff4d4f',
  text: '#d1d5e0',
  textMid: '#9da3bc',
  muted: '#6b7090',
  dimmed: '#454a60',
  mono: '"JetBrains Mono", "Consolas", monospace',
  spanStrikeBg: '#3d2000',
  spanMaskBg: '#0d3020',
  spanMaskColor: '#5de8a8',
  excerptOriginalBadgeBg: '#1a1c2e',
};

/** Light surfaces aligned with chat / enterprise light UI (indigo family, neutral grays) */
const LIGHT = {
  bg: '#ffffff',
  cardBg: '#f8fafc',
  footerBg: '#f1f5f9',
  border: '#e2e8f0',
  borderIn: '#e5e7eb',
  inputAcc: '#2563eb',
  inputBg: '#eff6ff',
  inputBdr: '#bfdbfe',
  contextAcc: '#0d9488',
  contextBg: '#f0fdfa',
  contextBdr: '#99f6e4',
  outputAcc: '#7c3aed',
  outputBg: '#f5f3ff',
  outputBdr: '#ddd6fe',
  green: '#059669',
  greenBg: '#d1fae5',
  amber: '#d97706',
  amberBg: '#fffbeb',
  red: '#dc2626',
  text: '#111827',
  textMid: '#374151',
  muted: '#6b7280',
  dimmed: '#64748b',
  mono: '"JetBrains Mono", "Consolas", monospace',
  spanStrikeBg: '#fef3c7',
  spanMaskBg: '#dcfce7',
  spanMaskColor: '#166534',
  excerptOriginalBadgeBg: '#f1f5f9',
};

const PII_LABEL = { email: 'Email', person_name: 'Person', phone: 'Phone', ip_address: 'IP', credit_card: 'Card', ssn: 'SSN' };

const GuardrailsPanel = ({ guardrailsInfo: gi }) => {
  const [open, setOpen] = useState(false);
  const themeStyles = useThemeStyles();
  const T = themeStyles.isLight ? LIGHT : DARK;

  if (!gi?.applied) return null;

  const ig = gi.inputGuard || {};
  const og = gi.outputGuard || {};
  const cg = gi.contextGuard || {};

  // What's configured
  const inputPolicies = ig.policiesRun || [];
  const outputPolicies = og.policiesRun || [];
  const inputConfigured = inputPolicies.length > 0;
  const outputConfigured = outputPolicies.length > 0;
  const contextApplied = !!cg.applied;

  // What happened
  const inputTriggered = !!ig.triggered;
  const outputTriggered = !!og.triggered;
  const contextModified = cg.chunksModified || 0;
  const contextRemoved = cg.chunksRemoved || 0;
  const contextActed = contextModified > 0 || contextRemoved > 0;
  const inputCount = (ig.changedSpans || []).length;
  const outputCount = (og.changedSpans || []).length;
  const anyActed = inputTriggered || outputTriggered || contextActed;

  // Don't show if nothing configured or nothing happened
  if (!anyActed) return null;

  // Determine what action was taken per stage
  const inputBlocked = inputTriggered && inputCount === 0;
  const inputMasked = inputTriggered && inputCount > 0;
  const outputBlocked = outputTriggered && outputCount === 0;
  const outputMasked = outputTriggered && outputCount > 0;

  // Build dynamic sections
  const sections = [];

  if (inputConfigured && inputTriggered) {
    sections.push({
      key: 'input',
      label: 'Input Guard',
      color: T.inputAcc,
      bg: T.inputBg,
      bdr: T.inputBdr,
      status: inputBlocked ? 'blocked' : 'masked',
      value: inputBlocked ? 'Blocked' : `${inputCount} masked`,
      detail: inputBlocked ? 'Query blocked — content violated safety policy' : 'PII detected and masked before sending to LLM',
      policies: inputPolicies,
    });
  }

  if (contextApplied && contextActed) {
    sections.push({
      key: 'context',
      label: 'Context Guard',
      color: T.contextAcc,
      bg: T.contextBg,
      bdr: T.contextBdr,
      status: 'masked',
      value: `${contextModified + contextRemoved} sanitized`,
      detail: `${contextModified} chunk${contextModified !== 1 ? 's' : ''} had PII masked, ${contextRemoved} removed — before LLM prompt`,
      policies: (cg.guards || []).map((name) => ({ name, result: contextActed ? 'masked' : 'passed' })),
    });
  }

  if (outputConfigured && outputTriggered) {
    sections.push({
      key: 'output',
      label: 'Output Guard',
      color: T.outputAcc,
      bg: T.outputBg,
      bdr: T.outputBdr,
      status: outputBlocked ? 'blocked' : 'masked',
      value: outputBlocked ? 'Blocked' : `${outputCount} masked`,
      detail: outputBlocked ? 'Response blocked — content violated safety policy' : 'PII detected and masked before showing to you',
      policies: outputPolicies,
    });
  }

  if (!sections.length) return null;

  const statusColor = (s) => (s === 'blocked' ? T.red : s === 'masked' ? T.amber : T.green);

  const chipBorder = themeStyles.isLight ? '1px solid #d97706' : `1px solid ${T.amber}25`;
  const strikeColor = themeStyles.isLight ? '#b45309' : T.amber;

  /** Expanded panel: light theme uses a grey shell so it does not blend into the white chat column */
  const expandedShellBg = themeStyles.isLight ? '#f1f5f9' : T.bg;
  const expandedShellBorder = themeStyles.isLight ? '#cbd5e1' : T.border;
  const expandedShellShadow = themeStyles.isLight ? '0 2px 10px rgba(15, 23, 42, 0.07)' : 'none';

  return (
    <div style={{ marginTop: 8 }}>
      {/* Chip */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div
          onClick={() => setOpen((o) => !o)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 6,
            cursor: 'pointer',
            userSelect: 'none',
            background: T.amberBg,
            border: chipBorder,
          }}
        >
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.amber }} />
          <span style={{ fontSize: 11, color: T.amber, fontWeight: 500 }}>Guardrails applied</span>
          <span
            style={{
              fontSize: 10,
              color: T.dimmed,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            ▾
          </span>
        </div>
      </div>

      {/* Expanded */}
      <div
        style={{
          overflow: 'hidden',
          maxHeight: open ? 2000 : 0,
          opacity: open ? 1 : 0,
          transition: 'max-height 0.3s ease, opacity 0.2s ease',
          marginTop: open ? 8 : 0,
        }}
      >
        <div
          style={{
            borderRadius: 8,
            border: `1px solid ${expandedShellBorder}`,
            background: expandedShellBg,
            overflow: 'hidden',
            boxShadow: expandedShellShadow,
          }}
        >
          {/* Guard cards */}
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.borderIn}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${sections.length}, 1fr)`, gap: 8 }}>
              {sections.map((s) => (
                <div key={s.key} style={{ borderRadius: 6, padding: '10px 12px', background: s.bg, border: `1px solid ${s.bdr}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: s.color,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                      }}
                    >
                      {s.label}
                    </span>
                    <span style={{ fontSize: 9, fontWeight: 600, color: statusColor(s.status), textTransform: 'uppercase' }}>
                      {s.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: statusColor(s.status), marginTop: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 3 }}>{s.detail}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Input diff — only when text was actually modified */}
          {inputMasked && ig.originalQuery && ig.sanitizedQuery && ig.originalQuery !== ig.sanitizedQuery && (
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.borderIn}` }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: T.dimmed,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 8,
                }}
              >
                Input Diff
              </div>
              <div style={{ borderRadius: 6, overflow: 'hidden', border: `1px solid ${T.borderIn}`, background: T.footerBg }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', borderBottom: `1px solid ${T.borderIn}` }}>
                  <div style={{ padding: '6px 10px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: T.muted }}>ORIGINAL</div>
                  <div style={{ background: T.borderIn }} />
                  <div style={{ padding: '6px 10px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: T.inputAcc }}>SENT TO LLM</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr' }}>
                  <div style={{ padding: '10px 10px', fontFamily: T.mono, fontSize: 10, lineHeight: 1.8, color: T.textMid, wordBreak: 'break-word' }}>
                    {(() => {
                      let text = ig.originalQuery;
                      const parts = [];
                      let idx = 0;
                      (ig.changedSpans || []).forEach((sp, i) => {
                        const pos = text.indexOf(sp.originalText, idx);
                        if (pos >= 0) {
                          if (pos > idx) parts.push(<span key={`p${i}`}>{text.slice(idx, pos)}</span>);
                          parts.push(
                            <span
                              key={`d${i}`}
                              style={{
                                background: T.spanStrikeBg,
                                color: strikeColor,
                                borderRadius: 3,
                                padding: '1px 4px',
                                textDecoration: 'line-through',
                              }}
                            >
                              {sp.originalText}
                            </span>,
                          );
                          idx = pos + sp.originalText.length;
                        }
                      });
                      if (idx < text.length) parts.push(<span key="e">{text.slice(idx)}</span>);
                      return parts;
                    })()}
                  </div>
                  <div style={{ background: T.borderIn }} />
                  <div style={{ padding: '10px 10px', fontFamily: T.mono, fontSize: 10, lineHeight: 1.8, color: T.textMid, wordBreak: 'break-word' }}>
                    {(ig.sanitizedQuery || '').split(/(\[[A-Z_]+_MASKED\])/).map((part, i) =>
                      /^\[.*_MASKED\]$/.test(part) ? (
                        <span
                          key={i}
                          style={{
                            background: T.spanMaskBg,
                            color: T.spanMaskColor,
                            borderRadius: 3,
                            padding: '1px 4px',
                            fontFamily: T.mono,
                          }}
                        >
                          {part}
                        </span>
                      ) : (
                        <span key={i}>{part}</span>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Output excerpts — only when output was masked (not blocked) */}
          {outputMasked && (og.changedSpans || []).length > 0 && (
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.borderIn}` }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: T.dimmed,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 8,
                }}
              >
                Output Masking
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {og.changedSpans.map((sp, i) => (
                  <div key={i} style={{ background: T.cardBg, border: `1px solid ${T.borderIn}`, borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.6, wordBreak: 'break-word', marginBottom: 4 }}>
                      {(sp.excerptContext || sp.replacement).split(/(\[[A-Z_]+_MASKED\])/).map((part, j) =>
                        /^\[.*_MASKED\]$/.test(part) ? (
                          <span key={j} style={{ background: T.outputBg, color: T.outputAcc, borderRadius: 2, padding: '0 4px' }}>
                            {part}
                          </span>
                        ) : (
                          <span key={j}>{part}</span>
                        ),
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9 }}>
                      <span
                        style={{
                          color: T.muted,
                          background: T.excerptOriginalBadgeBg,
                          padding: '1px 5px',
                          borderRadius: 3,
                          fontFamily: T.mono,
                        }}
                      >
                        {sp.originalText}
                      </span>
                      <span style={{ color: T.dimmed }}>→</span>
                      <span
                        style={{
                          color: T.outputAcc,
                          background: T.outputBg,
                          border: `1px solid ${T.outputBdr}`,
                          padding: '1px 5px',
                          borderRadius: 3,
                          fontFamily: T.mono,
                        }}
                      >
                        {sp.replacement}
                      </span>
                      <span style={{ marginLeft: 'auto', color: T.muted }}>{PII_LABEL[sp.type] || sp.type}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Policies — only guards that actually ran */}
          <div style={{ padding: '12px 14px' }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: T.dimmed,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 8,
              }}
            >
              Policies Applied
            </div>
            {sections.map((section) => (
              <div key={section.key}>
                {(section.policies || []).map((p, i) => {
                  const pName = typeof p === 'string' ? p : p.name;
                  const policyLabel = pName === 'DetectPII' ? 'DetectPII (Powered By Presedio)' : pName;
                  const pResult = typeof p === 'string' ? section.status : p.result || section.status;
                  const dirColor = section.color;
                  const dirLabel = section.key.toUpperCase();
                  return (
                    <div key={`${section.key}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: statusColor(pResult) }} />
                      <span style={{ flex: 1, fontSize: 11, color: T.textMid }}>{policyLabel}</span>
                      <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: `${dirColor}15`, color: dirColor }}>
                        {dirLabel}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 500, color: statusColor(pResult) }}>{pResult}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ padding: '6px 14px', background: T.footerBg, borderTop: `1px solid ${T.borderIn}`, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 9, color: T.dimmed, flex: 1 }}>Guardrails AI</span>
            <span
              style={{
                fontSize: 9,
                color: T.dimmed,
                background: T.cardBg,
                border: `1px solid ${T.border}`,
                padding: '1px 6px',
                borderRadius: 6,
              }}
            >
              v0.3.1
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardrailsPanel;
